import { forwardRef, useState, useContext, useEffect } from "react";
import {
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { LanguageContext } from "../../context/LanguageContext";

const InputPro = forwardRef(function InputPro(
  {
    label,
    name,
    type = "text",
    value = "",
    onChange,
    onBlur,
    error,
    success,
    helperText,
    required = false,
    disabled = false,
    className = "",
    showPasswordToggle = true,
    autoComplete = "on",
    ...props
  },
  ref
) {
  const { lang } = useContext(LanguageContext);
  const isRTL = lang === "ar";
  const dir = isRTL ? "rtl" : "ltr";

  const [focused, setFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  /* =========================
      DETECT VALUE (AUTOFILL FIX)
  ========================= */
  useEffect(() => {
    if (value && value.toString().trim() !== "") {
      setHasValue(true);
    } else {
      setHasValue(false);
    }
  }, [value]);

  const inputType =
    type === "password" && showPassword ? "text" : type;

  return (
    <div className={`w-full space-y-1 ${className}`} dir={dir}>
      <div className="relative">
        {/* INPUT */}
        <input
          ref={ref}
          id={name}
          name={name}
          type={inputType}
          value={value}
          disabled={disabled}
          autoComplete={autoComplete}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            setFocused(false);
            onBlur && onBlur(e);
          }}
          placeholder=" "
          aria-invalid={!!error}
          className={`
            peer w-full h-12 px-4 pt-5
            rounded-lg
            bg-white dark:bg-gray-900
            border
            text-sm
            text-gray-900 dark:text-gray-100
            placeholder-transparent
            transition
            focus:outline-none focus:ring-2
            disabled:opacity-50 disabled:cursor-not-allowed
            ${isRTL ? "text-right" : "text-left"}
            ${
              error
                ? "border-red-500 focus:ring-red-500/20"
                : success
                ? "border-green-500 focus:ring-green-500/20"
                : "border-gray-300 dark:border-gray-700 focus:border-blue-600 focus:ring-blue-600/20"
            }
          `}
          {...props}
        />

        {/* LABEL */}
        <label
          htmlFor={name}
          className={`
            absolute px-1
            text-sm
            transition-all pointer-events-none
            ${
              focused || hasValue
                ? "top-1 text-xs text-blue-600 dark:text-blue-400"
                : "top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400"
            }
            ${isRTL ? "right-3" : "left-3"}
          `}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {/* PASSWORD TOGGLE */}
        {type === "password" && showPasswordToggle && (
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className={`
              absolute top-1/2 -translate-y-1/2
              text-gray-400 hover:text-gray-600
              dark:text-gray-500 dark:hover:text-gray-300
              ${isRTL ? "left-4" : "right-4"}
            `}
          >
            {showPassword ? (
              <EyeSlashIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        )}

        {/* STATUS ICON */}
        {success && !error && (
          <CheckCircleIcon
            className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-green-500 ${
              isRTL ? "left-4" : "right-4"
            }`}
          />
        )}

        {error && (
          <ExclamationCircleIcon
            className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-red-500 ${
              isRTL ? "left-4" : "right-4"
            }`}
          />
        )}
      </div>

      {/* BELOW TEXT */}
      {error && (
        <p
          className={`text-xs text-red-600 dark:text-red-400 ${
            isRTL ? "text-right" : ""
          }`}
        >
          {error}
        </p>
      )}

      {!error && success && (
        <p className="text-xs text-green-600 dark:text-green-400">
          {success}
        </p>
      )}

      {!error && !success && helperText && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {helperText}
        </p>
      )}
    </div>
  );
});

export default InputPro;
